import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, Shield, Copy, Zap, Code2, Database,
  ShieldCheck, Check, TrendingUp, ArrowUpRight, Users,
  Server, ShoppingCart, BarChart2, Monitor
} from 'lucide-react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import SimpleKakaoButton from '../Components/SimpleKakaoButton';
import HeroSwitchVisual from '../Components/Heroswitchvisual';
import FeatureShowcase from '../Components/FeatureShowcase';



const Home = () => {
  // 추천인 code 쿠키 저장
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    if (refCode) {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);
      document.cookie = `referral_code=${refCode}; expires=${expiryDate.toUTCString()}; path=/`;
    }
  }, []);

  // 스크롤 감지 상태
  const [scrolled, setScrolled] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const [stepsPaused, setStepsPaused] = useState(false);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  // 사용 사례
  const useCases = [
    {
      id: "ecommerce",
      industry: "E-Commerce",
      title: "구매 전환율 최적화",
      desc: "장바구니 포기율을 낮추고, 결제 페이지의 병목 현상을 해결하여 매출을 직접적으로 증대시킵니다.",
      metric: "+24.5%",
      metricLabel: "평균 매출 상승",
      icon: <ShoppingCart className="w-6 h-6" />,
      color: "blue",
      gradient: "from-blue-500/20 to-cyan-500/5",
      borderColor: "group-hover:border-blue-500/50"
    },
    {
      id: "saas",
      industry: "B2B SaaS",
      title: "가입 및 온보딩 개선",
      desc: "복잡한 회원가입 절차를 단순화하고, 랜딩 페이지 메시지를 최적화하여 리드 확보를 가속화합니다.",
      metric: "2.8x",
      metricLabel: "가입 전환 배수",
      icon: <Monitor className="w-6 h-6" />,
      color: "indigo",
      gradient: "from-indigo-500/20 to-purple-500/5",
      borderColor: "group-hover:border-indigo-500/50"
    },
    {
      id: "media",
      industry: "Media & Contents",
      title: "체류 시간 증대",
      desc: "독자가 더 오래 머물도록 콘텐츠 배치를 조정하고, 구독 버튼의 클릭률을 극대화합니다.",
      metric: "+180s",
      metricLabel: "평균 체류시간",
      icon: <BarChart2 className="w-6 h-6" />,
      color: "rose", // 미디어는 붉은 계열이 주목도가 높음
      gradient: "from-rose-500/20 to-orange-500/5",
      borderColor: "group-hover:border-rose-500/50"
    }
  ];

  // 자동 롤링 (hover/focus 시 정지, 모션 최소화 설정 시 비활성)
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || stepsPaused) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, [stepsPaused]);

  const steps = [
    {
      id: 0,
      title: "1분 연동",
      desc: "복잡한 설치 과정은 없습니다. 스크립트 한 줄을 복사해서 붙여넣기만 하면 모든 준비가 끝납니다.",
      icon: <Code2 className="w-5 h-5" />,
      badge: "개발자 불필요"
    },
    {
      id: 1,
      title: "실험 생성",
      desc: "테스트하고 싶은 요소를 선택하고, 새로운 디자인을 적용하세요. 비주얼 에디터로 클릭 몇 번이면 충분합니다.",
      icon: <Zap className="w-5 h-5" />,
      badge: "노코드 설정"
    },
    {
      id: 2,
      title: "성장 확인",
      desc: "실시간으로 쌓이는 데이터를 확인하세요. 통계적으로 유의미한 승자가 결정되면 즉시 적용하여 매출을 높입니다.",
      icon: <TrendingUp className="w-5 h-5" />,
      badge: "자동 리포트"
    }
  ];

  const specs = [
    {
      label: "Performance",
      title: "사이트 속도 저하 0%",
      desc: "비동기 로딩으로 로딩 속도에 영향을 주지 않는 초경량 스크립트 (5kb 미만)",
      icon: <Zap className="w-5 h-5 text-yellow-500" />,
      status: "Fast"
    },
    {
      label: "Security",
      title: "GDPR & CCPA 준수",
      desc: "모든 데이터는 암호화되어 저장되며, 개인정보 보호 규정을 철저히 준수합니다.",
      icon: <ShieldCheck className="w-5 h-5 text-green-500" />,
      status: "Secure"
    },
    {
      label: "Reliability",
      title: "99.99% 가동률 보장",
      desc: "AWS 기반의 오토스케일링 인프라로 트래픽이 폭주해도 데이터 누락이 없습니다.",
      icon: <Server className="w-5 h-5 text-blue-500" />,
      status: "Stable"
    },
    {
      label: "Data Ownership",
      title: "데이터 소유권 100%",
      desc: "수집된 모든 고객 데이터의 소유권은 고객사에게 있으며 언제든 추출 가능합니다.",
      icon: <Database className="w-5 h-5 text-purple-500" />,
      status: "Yours"
    }
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText('<script src="https://cdn.switchup.com/ab.js"></script>');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };



  return (
    <div className="bg-white overflow-hidden">
      {/* 플로팅 CTA 버튼 (스크롤 시 표시) */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} motion-reduce:transition-none motion-reduce:transform-none`}>
        <Link to="/freestart">
          <button className="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group motion-reduce:transition-none">
            <span className="font-medium">무료로 시작하기</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 motion-reduce:transform-none transition-transform motion-reduce:transition-none" />
          </button>
        </Link>
      </div>

      <Header />
      {/* ⭐ 히어로: 광고비 vs 전환율 헤드라인 + 인터랙티브 스위치 비주얼 ⭐ */}
      <section className="pt-32 pb-0 relative overflow-hidden bg-gray-50">
        {/* 배경: 은은한 격자 무늬 or 그라데이션 */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

        {/* 상단 텍스트 영역 (중앙 정렬) */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800 mb-6 hover:bg-blue-100 transition-colors cursor-pointer">
            <span className="flex h-2 w-2 rounded-full bg-blue-600 mr-2 animate-pulse"></span>
            비즈니스 성장을 위한 전환율 최적화 솔루션
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">
            광고비를 늘리지 말고,<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">전환율</span>을 올리세요
          </h1>

          <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed break-keep">
            전환율은 감이 아니라 실험입니다.<br />
            A/B 테스트 · 히트맵 · 퍼널 · 세션 녹화 · 개인화 · 팝업으로<br />
            오늘 첫 실험을 시작하세요.<br />
            개발자 없이, 코드 한 줄 — <br className="sm:hidden" />
            1분이면 충분합니다.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to="/freestart">
              <button className="w-full sm:w-auto px-8 py-4 bg-gray-900 text-white rounded-lg shadow-lg hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-bold text-lg">
                무료로 시작하기
              </button>
            </Link>
            <Link to="/service/ab-test">
              <button className="w-full sm:w-auto px-8 py-4 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 font-medium text-lg shadow-sm">
                더 알아보기
              </button>
            </Link>
          </div>
        </div>

        {/* ===== 히어로 비주얼 ③안: 인터랙티브 SWITCHUP 스위치 (모바일/데스크톱 분기 내장) ===== */}
        <HeroSwitchVisual />
      </section>


      {/* ⭐ 기능 쇼케이스 (탭 전환) — A/B · 히트맵 · 퍼널 · 세션 녹화 · 개인화 · 팝업 ⭐ */}
      <FeatureShowcase />


      {/* 성장 선순환 워크플로우 (기존 히트맵 섹션에서 분리) */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* 배경 그리드 패턴 */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px] opacity-20"></div>
        {/* 배경 글로우 효과 */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-96 bg-blue-500/10 rounded-full blur-[100px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">데이터 기반 성장의 선순환</h2>
            <p className="text-slate-400">문제 발견부터 해결까지 끊김없이 연결됩니다</p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-purple-500/50 via-orange-500/50 via-cyan-500/50 via-blue-500/50 via-pink-500/50 to-emerald-500/50 -translate-y-1/2 z-0"></div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-6 relative z-10">
              {[
                {
                  step: "01",
                  title: "퍼널 분석",
                  desc: "어디서 고객을 잃는지 발견",
                  icon: "📊",
                  borderColor: "border-purple-500",
                  shadow: "shadow-purple-500/20"
                },
                {
                  step: "02",
                  title: "히트맵 분석",
                  desc: "어떤 경향으로 이탈하는지 파악",
                  icon: "🔍",
                  borderColor: "border-orange-500",
                  shadow: "shadow-orange-500/20"
                },
                {
                  step: "03",
                  title: "세션 녹화",
                  desc: "이탈 순간을 화면으로 확인",
                  icon: "🎬",
                  borderColor: "border-cyan-500",
                  shadow: "shadow-cyan-500/20"
                },
                {
                  step: "04",
                  title: "A/B 테스트",
                  desc: "가설 수립 및 실험 검증",
                  icon: "⚖️",
                  borderColor: "border-blue-500",
                  shadow: "shadow-blue-500/20"
                },
                {
                  step: "05",
                  title: "개인화",
                  desc: "검증된 내용을 고객별로 적용",
                  icon: "👥",
                  borderColor: "border-pink-500",
                  shadow: "shadow-pink-500/20"
                },
                {
                  step: "06",
                  title: "팝업 · 배너",
                  desc: "이탈 직전 즉시 개입",
                  icon: "🎯",
                  borderColor: "border-emerald-500",
                  shadow: "shadow-emerald-500/20"
                }
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center text-center group">
                  <div className={`w-16 h-16 rounded-2xl bg-slate-800 border-2 ${item.borderColor} flex items-center justify-center text-3xl mb-4 shadow-lg ${item.shadow} group-hover:-translate-y-2 transition-transform duration-300 relative`}>
                    {item.icon}
                    <div className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-slate-700 border border-slate-600 text-xs flex items-center justify-center font-bold text-white">
                      {item.step}
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-1">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              복잡한 과정은 걷어내고 <br className="sm:hidden" />
              <span className="text-blue-600">핵심</span>만 남겼습니다
            </h2>
            <p className="text-xl text-gray-600">
              시작부터 데이터 확인까지, 커피 한 잔 <br className="sm:hidden" /> 마실 시간이면 충분합니다.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* 왼쪽: 인터랙티브 메뉴 */}
            <div
              className="space-y-4"
              onMouseEnter={() => setStepsPaused(true)}
              onMouseLeave={() => setStepsPaused(false)}
              onFocusCapture={() => setStepsPaused(true)}
              onBlurCapture={() => setStepsPaused(false)}
            >
              {steps.map((step, index) => (
                <button
                  key={index}
                  type="button"
                  aria-pressed={activeStep === index}
                  onClick={() => setActiveStep(index)}
                  className={`relative w-full text-left p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${activeStep === index
                    ? 'bg-white border-blue-600 shadow-xl scale-[1.02]'
                    : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200'
                    }`}
                >
                  <div className="flex items-start gap-4">
                    {/* 아이콘 원 */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${activeStep === index ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                      {step.icon}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`text-xl font-bold ${activeStep === index ? 'text-gray-900' : 'text-gray-500'}`}>
                          {step.title}
                        </h3>
                        {activeStep === index && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {step.badge}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm leading-relaxed ${activeStep === index ? 'text-gray-600' : 'text-gray-400'}`}>
                        {step.desc}
                      </p>
                    </div>
                  </div>

                  {/* 진행률 바 (Active일 때만 표시) */}
                  {activeStep === index && (
                    <div className="absolute bottom-0 left-6 right-6 h-1 bg-gray-100 rounded-full overflow-hidden mt-6">
                      <div className="h-full bg-blue-600 animate-[progress_4s_linear]"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* 오른쪽: 다이나믹 비주얼 (스탭별로 바뀜) */}
            <div className="relative h-[400px] bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden flex items-center justify-center p-8 transition-all duration-500">
              {/* 배경 장식 */}
              <div className="absolute inset-0 bg-gray-50/50"></div>
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-50 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-indigo-50 rounded-full blur-3xl"></div>

              {/* Step 1: Code Snippet */}
              <div className={`absolute transition-all duration-500 transform ${activeStep === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
                <div className="bg-gray-900 rounded-xl p-6 shadow-2xl w-[320px] sm:w-[380px]">
                  <div className="flex gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="font-mono text-xs text-blue-300 mb-2">&lt;!-- Just Copy & Paste --&gt;</div>
                  <div className="font-mono text-sm text-white bg-black/30 p-4 rounded-lg border border-white/10">
                    <span className="text-purple-400">script</span> src=<span className="text-green-400">"cdn.switchup.com/ab.js"</span><br />
                    key=<span className="text-yellow-400">"YOUR_API_KEY"</span>
                  </div>
                  <div className="mt-4 flex justify-center">
                    <div className="inline-flex items-center px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                      <Check className="w-3 h-3 mr-1" /> Copied to clipboard
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Visual Editor UI */}
              <div className={`absolute w-full px-8 transition-all duration-500 transform ${activeStep === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
                <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <div className="text-sm font-bold text-gray-700">테스트 에디터</div>
                    <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">B안 (변경안)</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                      <span className="text-sm text-gray-500">버튼 색상</span>
                      <div className="flex gap-1">
                        <div className="w-5 h-5 rounded bg-blue-600 ring-2 ring-blue-200"></div>
                        <div className="w-5 h-5 rounded bg-red-500 opacity-30"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                      <span className="text-sm text-gray-500">헤드라인</span>
                      <span className="text-xs text-gray-400 bg-white px-2 py-1 border rounded">텍스트 수정...</span>
                    </div>
                    <button className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium mt-2">
                      테스트 시작 🚀
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 3: Result Chart */}
              <div className={`absolute transition-all duration-500 transform ${activeStep === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
                <div className="text-center">
                  <div className="inline-block p-4 bg-white rounded-2xl shadow-xl border border-blue-100 mb-4 relative">
                    <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-bounce">
                      +24%
                    </div>
                    <div className="flex items-end gap-3 h-32 w-48 justify-center pb-2 border-b border-gray-100">
                      <div className="w-8 bg-gray-200 rounded-t-lg h-16 relative group">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-gray-400">A</div>
                      </div>
                      <div className="w-8 bg-gradient-to-t from-blue-600 to-indigo-500 rounded-t-lg h-24 relative shadow-lg shadow-blue-500/30">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-blue-600 font-bold">B</div>
                      </div>
                    </div>
                    <div className="text-sm font-bold text-gray-800 mt-2">Winner Found!</div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/freestart">
              <button className="inline-flex items-center px-8 py-4 bg-gray-900 text-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 font-bold">
                <span>지금 무료로 1단계 시작하기</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </Link>
            <p className="mt-4 text-sm text-gray-500">
              * 신용카드 등록 없이 바로 시작할 수 있습니다
            </p>
          </div>

        </div>

        <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      </section>

      <section className="py-24 bg-[#0B0F19] text-white relative overflow-hidden">
        {/* 배경 장식 (은은한 빛) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* 헤더 */}
          <div className="text-center max-w-5xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              모든 비즈니스 목표를 위한 <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">검증된 성장 전략</span>
            </h2>
            <p className="text-lg text-gray-400 ">
              산업별로 가장 중요한 지표(Key Metric)를 <br className="sm:hidden" />움직입니다.
              SWITCHUP으로 달성한 실제 <br className="sm:hidden" />성과를 확인하세요.
            </p>
          </div>

          {/* 카드 그리드 */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {useCases.map((item, index) => (
              <div
                key={index}
                className={`group relative bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-1 overflow-hidden transition-all duration-500 hover:-translate-y-2 ${item.borderColor}`}
              >
                {/* 내부 그라데이션 (Hover시 강화) */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                <div className="relative h-full bg-[#0B0F19]/80 rounded-xl p-6 flex flex-col">

                  {/* 상단: 아이콘 & 산업명 */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-3 rounded-lg bg-${item.color}-500/10 text-${item.color}-400 ring-1 ring-${item.color}-500/20 group-hover:bg-${item.color}-500 group-hover:text-white transition-colors duration-300`}>
                      {item.icon}
                    </div>
                    <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">{item.industry}</span>
                  </div>

                  {/* 중단: 타이틀 & 설명 */}
                  <div className="mb-8 flex-1">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-100 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>

                  {/* 하단: 데이터 위젯 (Visual Metric) */}
                  <div className="relative mt-auto">
                    <div className="bg-white/5 rounded-lg p-4 border border-white/5 group-hover:border-white/10 transition-colors">
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase font-semibold mb-1">{item.metricLabel}</div>
                          <div className={`text-3xl font-bold text-white tracking-tight`}>
                            {item.metric}
                          </div>
                        </div>

                        {/* 상승 그래프 아이콘 */}
                        <div className={`p-2 rounded-full bg-${item.color}-500/20 text-${item.color}-400 mb-1`}>
                          <ArrowUpRight className="w-5 h-5" />
                        </div>
                      </div>

                      {/* 가짜 그래프 라인 (CSS Animation) */}
                      <div className="w-full h-1 bg-gray-700 rounded-full overflow-hidden">
                        <div className={`h-full bg-${item.color}-500 w-0 group-hover:w-full transition-all duration-1000 ease-out rounded-full`}></div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>

          {/* 하단 추가 문구 */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300">
              <Users className="w-4 h-4 text-blue-400" />
              <span>이미 <strong>수많은 기업</strong>이 데이터로 성장하고 있습니다</span>
            </div>
          </div>

        </div>
      </section>

      {/* 핵심 가치 제안 */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 섹션 헤더 (작게) */}
          <div className="flex items-center justify-center gap-2 mb-10 opacity-60">
            <div className="h-px w-12 bg-gray-300"></div>
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Enterprise Grade Quality</span>
            <div className="h-px w-12 bg-gray-300"></div>
          </div>

          {/* 스펙 그리드 */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specs.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-300 group">

                {/* 상단 라벨 & 상태 점 */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-gray-400 uppercase">{item.label}</span>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-50 border border-gray-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-[10px] font-medium text-gray-600">{item.status}</span>
                  </div>
                </div>

                {/* 아이콘 & 타이틀 */}
                <div className="flex items-start gap-3 mb-2">
                  <div className="mt-0.5 p-1.5 rounded-lg bg-gray-50 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 leading-tight">
                    {item.title}
                  </h3>
                </div>

                {/* 설명 */}
                <p className="text-sm text-gray-500 leading-relaxed pl-[42px]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        {/* 1. 배경: 강렬한 그라데이션 (Deep Blue to Purple) */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-900 to-slate-900 z-0"></div>

        {/* 2. 배경 장식: 역동적인 패턴 */}
        <div className="absolute inset-0 opacity-20 z-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/20 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* 왼쪽: 설득 문구 */}
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 font-semibold text-sm mb-6 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-yellow-300" />
                <span>지금 시작하면 30일 무료</span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                비즈니스 성장을 위한<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">마지막 퍼즐 조각</span>
              </h2>

              <p className="text-lg text-blue-100/80 mb-8 max-w-xl leading-relaxed">
                더 이상 개발팀 일정을 기다리지 마세요.<br />
                마케터가 직접 실험하고, 디자이너가 직접 수정하는 세상을 만듭니다.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/freestart">
                  <button className="w-full sm:w-auto px-8 py-4 bg-white text-blue-900 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] hover:-translate-y-1 flex items-center justify-center gap-2">
                    <Zap className="w-5 h-5 fill-current" />
                    지금 무료로 시작하기
                  </button>
                </Link>
                <Link to="/contact">
                  <button className="w-full sm:w-auto px-8 py-4 bg-transparent border border-white/20 text-white rounded-xl font-medium hover:bg-white/10 transition-all flex items-center justify-center">
                    도입 문의하기
                  </button>
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-6 text-sm text-blue-200/60">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" /> 카드 등록 없음
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> 언제든 취소 가능
                </div>
              </div>
            </div>

            {/* 오른쪽: 코드 스니펫 (Interactive) */}
            <div className="relative group">
              {/* 뒤쪽 글로우 효과 */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-1000"></div>

              <div className="relative bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
                <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                  <div className="text-xs font-mono text-slate-500">install-snippet.js</div>
                </div>

                {/* 코드 내용 */}
                <div className="font-mono text-sm sm:text-base leading-relaxed mb-6 group-hover:scale-[1.01] transition-transform duration-300">
                  <div className="text-slate-400">&lt;!-- Copy this line to your &lt;head&gt; --&gt;</div>
                  <div className="mt-2 text-white break-all">
                    <span className="text-purple-400">&lt;script</span> <span className="text-sky-300">src</span>=<span className="text-green-400">"https://cdn.switchup.com/ab.js"</span>
                    <br className="hidden sm:block" />
                    <span className="ml-0 sm:ml-2"></span>
                    <span className="text-sky-300">data-key</span>=<span className="text-yellow-400">"YOUR_API_KEY"</span><span className="text-purple-400">&gt;&lt;/script&gt;</span>
                  </div>
                </div>

                {/* 복사 버튼 */}
                <button
                  onClick={handleCopy}
                  className="w-full py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-center justify-center gap-2 text-white group-active:scale-95"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium">복사되었습니다!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>코드 복사하기</span>
                    </>
                  )}
                </button>

                {/* 하단 툴팁 */}
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-full text-center">
                  <div className="inline-block bg-slate-800 text-slate-300 text-xs px-3 py-1 rounded-full border border-slate-700 shadow-lg animate-bounce">
                    👇 10초 설치 완료
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      {/* 푸터 */}
      <Footer />

    </div>
  );
};

export default Home;